import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import {
    ArrowRightLongIcon,
    CheckIcon
} from "@solomon-labs/design/components/icons";
import { AnimatePresence, motion } from "motion/react";
import { type ReactElement, useEffect, useRef, useState } from "react";

import type { NextStepTarget } from "../types";

/**
 * Bottom-of-form Save / Saving / Saved / Continue action.
 *
 * Centralises the state machine that every structured onboarding form
 * used to inline as an IIFE — UseCase, LegalEntity, TaxId, Signatory,
 * FundsNarrative. Each form computes `submittable` + `submitting` +
 * `isSavedClean` from its own field state and hands them in; this
 * component owns the resulting button label, disabled flag, trailing
 * icon, and the inline error line that lived beside the button in the
 * old shape.
 *
 * Behavior matrix (precedence top → bottom):
 *   1. `submitting`               → "Saving…"   (disabled)
 *   2. `isSavedClean` + nextStep  → "Continue to {title} →"  (enabled)
 *      → clicking calls `onContinue`
 *   3. `isSavedClean`             → "Saved"     (disabled, terminal)
 *   4. `!submittable`             → "Save"      (disabled, invalid)
 *      → `incompleteHint` names what is missing
 *   5. default                    → "Save"      (enabled)
 *      → clicking calls `onSave`
 *
 * Per-entry forms (PersonDetail, Wallet) and external-flow panels
 * (MsaSigningPanel) intentionally don't use this — their CTAs aren't
 * Save+Continue.
 */
export interface FormPrimaryActionState {
    submittable: boolean;
    submitting: boolean;
    /** True when the form's last successful submit matches the
     *  current field state — i.e. there's nothing new to save. */
    isSavedClean: boolean;
    /** When set together with `isSavedClean`, the action flips to the
     *  forward CTA and clicking calls `onContinue`. Null when there's
     *  no next incomplete subsection (last step, or all done). */
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
    onSave: () => void;
    /** Inline error surfaced under the button on save failure. */
    error?: string | null;
    /**
     * What is still outstanding, shown under a disabled Save. A button
     * that refuses to enable has to say what it is waiting for —
     * otherwise the only way to find out is to hunt the form for the
     * empty field (or, worse, to assume the section is done).
     */
    incompleteHint?: string | null;
}

/** How long the "Saved" confirmation pill stays visible after a
 *  successful save before fading out. Chosen to be long enough to
 *  register but short enough not to linger after the user has moved on
 *  to the next subsection via Continue. */
const SAVED_FLASH_MS = 2500;

function continueTitle(title: string): string {
    return title === "Master Services Agreement" ? "Agreement" : title;
}

export function FormPrimaryAction(props: FormPrimaryActionState): ReactElement {
    const showContinue =
        props.isSavedClean &&
        props.nextStep !== null &&
        props.nextStep !== undefined &&
        props.onContinue !== undefined;
    const label = props.submitting
        ? "Saving…"
        : showContinue && props.nextStep
          ? `Continue to ${continueTitle(props.nextStep.title)}`
          : props.isSavedClean
            ? "Saved"
            : "Save";
    const disabled = showContinue
        ? false
        : !props.submittable || props.submitting || props.isSavedClean;
    const handleClick = (): void => {
        if (showContinue) {
            props.onContinue?.();
            return;
        }
        props.onSave();
    };

    // Flash a "Saved" pill above the button on the false → true
    // transition of isSavedClean. Mounting in a saved-clean state
    // (revisiting a previously-saved subsection) is not a fresh save,
    // so we skip those — `wasSavedClean.current` is seeded with the
    // initial prop value via lazy ref init.
    const [justSaved, setJustSaved] = useState(false);
    const wasSavedClean = useRef(props.isSavedClean);
    useEffect(() => {
        if (!props.isSavedClean) {
            wasSavedClean.current = false;
            setJustSaved(false);
            return;
        }

        if (!wasSavedClean.current && props.isSavedClean) {
            setJustSaved(true);
            wasSavedClean.current = true;
            const t = setTimeout(() => setJustSaved(false), SAVED_FLASH_MS);
            return (): void => clearTimeout(t);
        }

        wasSavedClean.current = true;
    }, [props.isSavedClean]);

    return (
        <div className="pt-12 max-w-sm mx-auto">
            {/* Relative wrapper around the Button so the "Saved" pill
                can float above it (bottom-full) without affecting
                layout — content below the button stays put when the
                pill mounts / unmounts. */}
            <div className="relative">
                <AnimatePresence>
                    {justSaved && (
                        <motion.div
                            key="just-saved"
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: 0.2,
                                ease: easings.outCubic
                            }}
                            className="absolute bottom-full left-0 right-0 mb-2 flex items-center justify-center gap-1.5 text-success-base pointer-events-none"
                        >
                            <CheckIcon className="size-4" />
                            <Text variant="label" className="leading-none">
                                Saved
                            </Text>
                        </motion.div>
                    )}
                </AnimatePresence>
                <Button
                    variant="raised"
                    interaction="lifted"
                    size="sm"
                    fullWidth
                    disabled={disabled}
                    trailing={
                        showContinue ? (
                            <ArrowRightLongIcon className="size-5" />
                        ) : undefined
                    }
                    onClick={handleClick}
                >
                    {label}
                </Button>
            </div>
            {!props.error &&
                props.incompleteHint &&
                !props.submittable &&
                !props.submitting && (
                    <Text
                        variant="small"
                        className="block leading-snug text-foreground-muted text-center pt-3"
                    >
                        {props.incompleteHint}
                    </Text>
                )}
            {props.error && (
                <Text
                    variant="small"
                    className="block leading-snug text-error-base text-center pt-3"
                >
                    {props.error}
                </Text>
            )}
        </div>
    );
}
